import { Linkedin, Mic, CalendarClock, GraduationCap, Building2, Target } from "lucide-react";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem, MaskedLines } from "@/components/shared/Reveal";
import { PrimaryButton, SecondaryButton } from "@/components/shared/Buttons";
import { useSeo } from "@/hooks/useSeo";
import { FOUNDER, ASSETS } from "@/lib/site";

export default function Founder() {
  useSeo(
    "Founder – Pankaj Sharma",
    "Pankaj Sharma, Founder & CEO of ASAI — Technology Consultant, AI & Digital Payments Expert and Corporate Trainer with 15+ years of industry experience."
  );

  return (
    <div data-testid="page-founder">
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-16 pt-32 md:pb-24 md:pt-40">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(45% 55% at 85% 5%, hsla(224,76%,48%,0.35), transparent), radial-gradient(40% 45% at 5% 30%, hsla(21,100%,50%,0.16), transparent)",
          }}
        />
        <div className="container-asai relative z-10 grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <span className="mb-5 block text-xs font-semibold uppercase tracking-[0.22em] text-orange">The Founder</span>
            <h1 className="font-heading text-4xl font-bold leading-[0.98] tracking-tight text-white md:text-6xl">
              <MaskedLines lines={[FOUNDER.name]} />
            </h1>
            <div className="mt-4 flex flex-col gap-0.5 text-white/70">
              {FOUNDER.titles.map((t) => (
                <span key={t} className="font-medium">{t}</span>
              ))}
            </div>
            <p className="mt-5 max-w-xl text-sm font-medium leading-relaxed text-orange/90">{FOUNDER.positioning}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <SecondaryButton href={FOUNDER.linkedin} onDark target="_blank" rel="noopener noreferrer" testId="founder-linkedin">
                <Linkedin className="h-4 w-4" /> Connect on LinkedIn
              </SecondaryButton>
              <PrimaryButton to="/contact" testId="founder-invite-speaker">
                <Mic className="h-4 w-4" /> Invite as Speaker
              </PrimaryButton>
              <SecondaryButton to="/contact" onDark testId="founder-request-session">Request Expert Session</SecondaryButton>
            </div>
          </div>

          <Reveal className="relative">
            <div className="relative overflow-hidden rounded-3xl border border-white/10" style={{ clipPath: "polygon(0 0, 100% 0, 100% 94%, 0 100%)" }}>
              <img src={ASSETS.founder} alt="Pankaj Sharma, Founder & CEO of ASAI" className="aspect-[4/5] w-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent" />
            </div>
            <div className="pointer-events-none absolute -bottom-6 -left-6 h-40 w-40 rounded-full bg-royal/30 blur-3xl" />
          </Reveal>
        </div>
      </section>

      {/* PROFILE + MISSION */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-asai grid gap-14 lg:grid-cols-[1fr_1fr]">
          <div className="flex flex-col gap-6">
            <SectionHeading overline="Profile" title="A technology leader at heart" />
            <p className="text-lg leading-relaxed text-navy/70">{FOUNDER.fullProfile}</p>
          </div>
          <Reveal delay={0.1} className="flex flex-col gap-5 rounded-3xl border border-navy/10 bg-lightgrey p-9">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-royal/10 text-royal"><Target className="h-6 w-6" /></span>
            <h3 className="font-heading text-2xl text-navy">Mission</h3>
            <p className="text-navy/70">{FOUNDER.mission}</p>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRY EXPOSURE TIMELINE */}
      <section className="bg-navy py-24 md:py-32">
        <div className="container-asai">
          <SectionHeading
            onDark
            overline="Career journey"
            title="Industry exposure"
            description="A snapshot of the enterprise environments and consulting engagements across the founder's professional career."
          />
          <div className="relative mt-14">
            <div className="absolute left-[19px] top-2 bottom-2 w-px bg-white/10 md:left-1/2" aria-hidden="true" />
            <StaggerGroup className="flex flex-col gap-8" stagger={0.08}>
              {FOUNDER.experience.map((exp, i) => (
                <StaggerItem
                  key={exp}
                  className={`relative flex items-start gap-6 md:w-1/2 ${i % 2 === 0 ? "md:self-start md:pr-12 md:text-right md:flex-row-reverse" : "md:self-end md:pl-12"}`}
                >
                  <span className="relative z-10 mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-orange/50 bg-navy text-orange">
                    <Building2 className="h-5 w-5" />
                  </span>
                  <div className="flex-1 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/45 md:justify-start">
                      <CalendarClock className="h-3.5 w-3.5" /> Experience {String(i + 1).padStart(2, "0")}
                    </div>
                    <p className="mt-2 font-heading text-lg text-white">{exp}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
          <p className="mt-10 max-w-2xl text-xs text-white/40">
            Note: The above reflects the founder&apos;s individual professional experience and exposure. It does not represent partnerships or endorsements of ASAI.
          </p>
        </div>
      </section>

      {/* EDUCATION */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-asai">
          <SectionHeading overline="Education" title="Academic background" />
          <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-3">
            {FOUNDER.education.map((e) => (
              <StaggerItem key={e.title} className="flex flex-col gap-4 rounded-2xl border border-navy/10 bg-lightgrey p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-royal/10 text-royal"><GraduationCap className="h-6 w-6" /></span>
                <h3 className="font-heading text-xl text-navy">{e.title}</h3>
                <p className="text-sm text-navy/60">{e.place}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, hsl(224 76% 40%), hsl(21 100% 50%))" }} />
        <div className="container-asai relative z-10 flex flex-col items-center gap-6 text-center">
          <h2 className="font-heading text-3xl font-bold text-white md:text-5xl">Invite Pankaj to your next event</h2>
          <p className="max-w-xl text-white/85">Expert sessions, corporate training and career mentorship for colleges and organisations.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <PrimaryButton to="/contact" size="lg" className="bg-white !text-navy hover:!bg-white" withArrow testId="founder-cta-session">Request Expert Session</PrimaryButton>
            <a href={FOUNDER.linkedin} target="_blank" rel="noopener noreferrer" data-testid="founder-cta-linkedin" className="inline-flex items-center gap-2 rounded-full border border-white/50 px-8 py-4 text-base font-medium text-white transition-colors hover:bg-white/10">
              <Linkedin className="h-4 w-4" /> Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
